import { ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import type { SortingState } from '@tanstack/react-table';
import { Select } from './forms';
import { FilterWrapper } from './product-table/filters/filter-wrapper';
import { ToolbarPopover } from './toolbar/popover';

export type SortColumnOption = {
  id: string;
  label: string;
  hideDirection?: boolean;
  /**
   * Direction to apply when the user picks this option. Omit to carry the
   * current direction over, which is the right behavior for a list of
   * like-typed columns. Set it where the natural reading order differs per
   * column — names want A-Z, money and dates want largest/newest first — so
   * switching sort column doesn't leave the user staring at Z-A.
   */
  defaultDesc?: boolean;
};

/**
 * Direction to apply when the user switches to `columnId`. The column's own
 * `defaultDesc` wins when it declares one; otherwise the current direction
 * carries over. Uses `??` rather than `||` on purpose — `defaultDesc: false`
 * ("sort this one ascending") is a real instruction, not an absent one.
 */
export function resolveSortDirection(
  options: SortColumnOption[],
  columnId: string,
  currentlyDescending: boolean
): boolean {
  return options.find((option) => option.id === columnId)?.defaultDesc ?? currentlyDescending;
}

export type SortPopoverProps = {
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  sortColumnOptions: SortColumnOption[];
};

export function SortPopover({ sorting, onSortingChange, sortColumnOptions }: SortPopoverProps) {
  const currentSort = sorting[0];
  const currentColumnId = currentSort?.id ?? sortColumnOptions[0]?.id ?? '';
  const isDescending = currentSort?.desc ?? true;

  const currentOption = sortColumnOptions.find((o) => o.id === currentColumnId);
  const showDirection = !currentOption?.hideDirection;

  const handleColumnChange = (columnId: string) => {
    onSortingChange([
      { id: columnId, desc: resolveSortDirection(sortColumnOptions, columnId, isDescending) },
    ]);
  };

  const handleDirectionChange = (desc: boolean) => {
    onSortingChange([{ id: currentColumnId, desc }]);
  };

  return (
    <ToolbarPopover icon={<ArrowsUpDownIcon className="size-5" />} label="Sort" minWidth="min-w-80">
      <FilterWrapper label="Sort by">
        <Select<string>
          value={currentColumnId}
          onChange={handleColumnChange}
          options={sortColumnOptions.map((option) => ({ value: option.id, label: option.label }))}
        />
      </FilterWrapper>

      {showDirection && (
        <FilterWrapper label="Order">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="direction"
                value="asc"
                checked={!isDescending}
                onChange={() => handleDirectionChange(false)}
                className="mr-2"
              />
              <span className="text-sm">Ascending</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="direction"
                value="desc"
                checked={isDescending}
                onChange={() => handleDirectionChange(true)}
                className="mr-2"
              />
              <span className="text-sm">Descending</span>
            </label>
          </div>
        </FilterWrapper>
      )}
    </ToolbarPopover>
  );
}
